import { Divider, Flex, Heading, Image, Link, Text } from '@chakra-ui/react';
import { useEffect } from 'react';

import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import { CONTACT_US } from '../../../constants/urls';
import { Affiliate, TemporaryData } from '../../../gql/generated/graphql';
import { CookieKeys, useCookie } from '../../../hooks/useCookie';
import { affiliateProviderLogo } from '../../../utils/affiliateProviders';
import getSearchPhoneNumbers from '../../../utils/getSearchPhoneNumbers';
import { capitalizeFirstLetter } from '../../../utils/strings';

const SinglePageSubmitHeader = ({
  affiliateTempInfoData,
  affiliateDetails,
  dealExists,
  lienholderNotAvailable,
}: {
  affiliateTempInfoData?: TemporaryData;
  affiliateDetails?: Affiliate | null;
  dealExists?: boolean;
  lienholderNotAvailable?: boolean;
}) => {
  const phoneNumber = getSearchPhoneNumbers();

  const canContinue = !dealExists && !lienholderNotAvailable;
  const [, setGuid] = useCookie(CookieKeys.GUID_KEY);

  useEffect(() => {
    if (lienholderNotAvailable) {
      setGuid(null);
    }
  }, [lienholderNotAvailable]);

  return (
    <Flex
      direction="column"
      rounded="10px"
      mx={{ md: 2 }}
      {...(!canContinue && {
        mb: 6,
        border: '1px solid',
        borderColor: 'gray.200',
        bgColor: 'white',
      })}
    >
      <Flex
        flexDir="column"
        px={6}
        py={3}
        alignItems={{ base: 'center', md: canContinue ? 'center' : 'flex-start' }}
        textAlign={{ base: 'center', md: canContinue ? 'center' : 'left' }}
      >
        <Image
          mb={6}
          w="180px"
          alt="logo"
          src={affiliateProviderLogo(affiliateDetails?.affiliate_provider?.slug)}
        />
        {canContinue ? (
          <Text fontSize="16px">
            Hi, {capitalizeFirstLetter(affiliateTempInfoData?.first_name)}! You're pre-approved with
            LendingTree. We just need a few more details from you.
          </Text>
        ) : (
          <>
            <Divider
              borderColor="grayDarkBackground"
              mb={5}
              mt={2}
              display={{ base: 'block', md: 'none' }}
            />
            <Heading as="h1" mb={2} fontSize="24px">
              Hi, {capitalizeFirstLetter(affiliateTempInfoData?.first_name)}!
            </Heading>
            <Text mb={6} fontSize="16px" maxW={{ base: '300px', md: 'unset' }}>
              {dealExists && (
                <>
                  Looks like you already have submitted a lease buyout with us. Please give us a
                  call at{' '}
                  <Link display="inline-block" href={`tel:+1-${phoneNumber}`}>
                    {phoneNumber}
                  </Link>
                  .
                </>
              )}
              {lienholderNotAvailable && (
                <>
                  Looks like we are unable to work with the lienholder on your lease. If you have
                  any questions please give us a call at{' '}
                  <Link display="inline-block" href={`tel:+1-${phoneNumber}`}>
                    {phoneNumber}
                  </Link>
                  .
                </>
              )}
            </Text>
            <PrimaryButton
              mb={4}
              onClick={() => {
                window.location.href = CONTACT_US;
              }}
            >
              CONTACT US
            </PrimaryButton>
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default SinglePageSubmitHeader;
